import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@baseline/web/supabase";

export interface Project {
	id: string;
	name: string;
	description: string | null;
	created_at: string;
	updated_at: string;
	last_synthesized_at: string | null;
	synthesis_due_at: string | null;
	document_count: number;
	source_count: number;
	synthesis_doc_id: string | null;
}

// CONTEXT: Generated Supabase types are stale — project_id on library_items and
// synthesis columns on projects were added in recent migrations not yet reflected
// in the generated types. We cast query results until types are regenerated.

interface ProjectRow {
	id: string;
	name: string;
	description: string | null;
	created_at: string;
	updated_at: string;
	last_synthesized_at: string | null;
	synthesis_due_at: string | null;
}

interface ItemWithProjectId {
	project_id: string;
}

interface ItemWithIdAndProjectId {
	id: string;
	project_id: string;
}

export function useProjects(userId: string | undefined) {
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ["projects", userId],
		queryFn: async (): Promise<Project[]> => {
			const { data, error } = await supabase
				.from("projects")
				.select(
					"id, name, description, created_at, updated_at, last_synthesized_at, synthesis_due_at",
				)
				.order("updated_at", { ascending: false });

			if (error) throw error;
			const projects = (data ?? []) as unknown as ProjectRow[];
			if (projects.length === 0) return [];

			const projectIds = projects.map((p) => p.id);

			// Fetch item counts and synthesis doc IDs in parallel
			const [docsRes, sourcesRes, synthesisRes] = await Promise.all([
				supabase
					.from("library_items")
					.select("project_id")
					.in("project_id", projectIds)
					.eq("source_type", "document")
					.eq("is_archived", false),
				supabase
					.from("library_items")
					.select("project_id")
					.in("project_id", projectIds)
					.neq("source_type", "document")
					.eq("is_archived", false),
				supabase
					.from("library_items")
					.select("id, project_id")
					.in("project_id", projectIds)
					.eq("source_type", "document")
					.eq("doc_type", "synthesis")
					.eq("is_archived", false),
			]);

			const docCounts = new Map<string, number>();
			for (const row of (docsRes.data ??
				[]) as unknown as ItemWithProjectId[]) {
				docCounts.set(
					row.project_id,
					(docCounts.get(row.project_id) ?? 0) + 1,
				);
			}

			const sourceCounts = new Map<string, number>();
			for (const row of (sourcesRes.data ??
				[]) as unknown as ItemWithProjectId[]) {
				sourceCounts.set(
					row.project_id,
					(sourceCounts.get(row.project_id) ?? 0) + 1,
				);
			}

			const synthesisDocs = new Map<string, string>();
			for (const row of (synthesisRes.data ??
				[]) as unknown as ItemWithIdAndProjectId[]) {
				synthesisDocs.set(row.project_id, row.id);
			}

			return projects.map((p) => ({
				id: p.id,
				name: p.name,
				description: p.description,
				created_at: p.created_at,
				updated_at: p.updated_at,
				last_synthesized_at: p.last_synthesized_at,
				synthesis_due_at: p.synthesis_due_at,
				document_count: docCounts.get(p.id) ?? 0,
				source_count: sourceCounts.get(p.id) ?? 0,
				synthesis_doc_id: synthesisDocs.get(p.id) ?? null,
			}));
		},
		enabled: !!userId,
	});

	useEffect(() => {
		if (!userId) return;

		const channel = supabase
			.channel(`projects:${userId}`)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "projects",
					filter: `user_id=eq.${userId}`,
				},
				() => {
					queryClient.invalidateQueries({
						queryKey: ["projects", userId],
					});
				},
			)
			.subscribe();

		return () => {
			supabase.removeChannel(channel);
		};
	}, [userId, queryClient]);

	return query;
}
